/**
 * Manifest-driven TanStack route builder (T9.7).
 *
 * Why this file exists
 * --------------------
 * Before T9.7, every new module had to:
 *   - declare its routes in `<module>/manifest.ts` (for `useModules()` /
 *     nav discovery), AND
 *   - hand-write a `createRoute(...)` block per page in `src/router.ts`,
 *     plus an entry in `PRODUCT_ROUTES` / `ADMIN_ROUTES`.
 *
 * That ~36-line tax per domain broke the "≤ 5% changes outside the
 * module" macro acceptance for T9 (spec §7 / §13). With this builder,
 * the manifest is the *sole* source of truth — discovery and route-tree
 * registration both read from it.
 *
 * Contract
 * --------
 * `buildModuleRoutes(rootRoute, surface)` walks every manifest discovered
 * via `import.meta.glob('@/modules/*\/manifest.ts', { eager: true })`,
 * filters by `surface` (defaulting to `'product'`), and emits a TanStack
 * `Route` object per `ModuleRoute`. Each generated route:
 *
 *   - has `getParentRoute: () => rootRoute`
 *   - has `path: <route.path>` (verbatim — paths in manifests already use
 *     TanStack's `$param` convention, e.g. `/finance/instances/$binId`)
 *   - has a `component` wrapped in `React.lazy(route.component)` so the
 *     route's chunk is split exactly as it was when statically declared
 *     in router.ts
 *   - has `validateSearch` if the manifest provided one (T9.7 added the
 *     optional field to `ModuleRoute` for this purpose)
 *
 * Constraints + non-goals
 * -----------------------
 * - Static auth routes (`/login`, `/signup`, `/entrar`), legacy product
 *   redirects (`/`, `/bills`, `/insights`, `/historico`), the
 *   console-host root routes (`/` → ConsoleHome, `/login` → ConsoleLogin)
 *   and the catch-all `$` 404 stay hand-written in router.ts. They are
 *   not module-owned — they are shell / framework concerns, and forcing
 *   them through a manifest would require ad-hoc fields (`beforeLoad`,
 *   custom redirect targets) that aren't worth the abstraction.
 * - This builder does NOT widen the registered-route type union. The
 *   wide `typeof typeOnlyTree` cast in router.ts continues to be the
 *   single source for `<Link to>` / `navigate({ to })` type inference;
 *   the route paths declared in manifests flow through that cast
 *   unchanged.
 *
 * Test surface — `src/lib/modules/__tests__/routeBuilder.test.ts`.
 */

import {
  createRoute,
  type AnyRootRoute,
  lazyRouteComponent,
} from '@tanstack/react-router'
import { type ModuleManifest, type ModuleSurface, type ModuleRoute } from './types'

const rawModules = import.meta.glob('@/modules/*/manifest.ts', {
  eager: true,
}) as Record<string, { manifest?: ModuleManifest }>

/**
 * Pure helper exposed for tests: returns every manifest in registration
 * order (path-sorted by glob, which is deterministic across runs).
 */
export function getAllManifests(): ModuleManifest[] {
  return Object.values(rawModules)
    .map((m) => m.manifest)
    .filter((m): m is ModuleManifest => Boolean(m))
}

/**
 * Returns the manifests whose `surface` matches the requested host
 * (defaulting to `'product'` when the field is omitted). Exposed for
 * tests + introspection; the router itself only needs the route output.
 */
export function manifestsForSurface(
  surface: ModuleSurface,
  manifests: ModuleManifest[] = getAllManifests(),
): ModuleManifest[] {
  return manifests.filter((m) => (m.surface ?? 'product') === surface)
}

/**
 * Build a flat list of TanStack child routes from every manifest whose
 * surface matches `surface`. The returned routes are children of
 * `rootRoute`; the caller (`router.ts`) is responsible for combining
 * them with the static shell routes via `rootRoute.addChildren([...])`.
 *
 * `manifestsOverride` exists for unit tests that want to inject a
 * fabricated registry without touching the on-disk modules.
 */
export function buildModuleRoutes(
  rootRoute: AnyRootRoute,
  surface: ModuleSurface,
  manifestsOverride?: ModuleManifest[],
) {
  const manifests = manifestsOverride
    ? manifestsForSurface(surface, manifestsOverride)
    : manifestsForSurface(surface)
  const seen = new Set<string>()
  // Result is intentionally typed loosely: see the comment on
  // `toTanStackRoute` — the per-route literal-path inference is restored
  // by `router.ts` via its `as unknown as typeof typeOnlyTree` cast.
  const result: ReturnType<typeof toTanStackRoute>[] = []
  for (const manifest of manifests) {
    for (const route of manifest.routes) {
      // Guard against duplicate path declarations across manifests —
      // TanStack's createRouter will throw at runtime if two children
      // share a path, but a friendly message here points at the manifest
      // that introduced the collision.
      if (seen.has(route.path)) {
        console.warn(
          `[routeBuilder] duplicate route path "${route.path}" in module ` +
            `"${manifest.module_id}" — skipping the second registration.`,
        )
        continue
      }
      seen.add(route.path)
      result.push(toTanStackRoute(rootRoute, route))
    }
  }
  return result
}

/**
 * Wrap a single `ModuleRoute` into a TanStack `createRoute()` call.
 * Extracted so the test suite can verify the conversion shape without
 * loading the full manifest registry.
 *
 * Implementation note on the `as never` cast
 * ------------------------------------------
 * `createRoute()` is heavily overloaded: the literal value of `path` is
 * baked into the route's full type so callers can `<Link to="/finance/...">`
 * with end-to-end inference. When `path` is widened to `string` (which
 * it must be here — we are iterating arbitrary manifests at runtime),
 * `tsc -b` rejects the call because the resulting `Route<...,'/',...>`
 * vs `Route<...,string,...>` shapes do not unify.
 *
 * The router-level fix is the existing `as unknown as typeof typeOnlyTree`
 * cast in `src/router.ts`, which re-narrows the entire tree against a
 * hand-rolled `TYPE_ONLY_ROUTES` array. Inside this builder we therefore
 * trade local strictness for a clear seam: `createRoute(options as never)`
 * tells TS "I know the literal types do not line up — the router-level
 * cast restores them". Callers see no loss of inference.
 */
export function toTanStackRoute(rootRoute: AnyRootRoute, route: ModuleRoute) {
  // `lazyRouteComponent` is TanStack's official escape hatch for code-
  // split route components: it accepts the same `() => Promise<{default}>`
  // shape that `React.lazy` accepts and integrates with the router's
  // pending / error boundaries. This preserves the per-route chunking
  // each manifest already declared via its dynamic `import()` call.
  const options: Record<string, unknown> = {
    getParentRoute: () => rootRoute,
    path: route.path,
    component: lazyRouteComponent(route.component),
  }
  if (route.validateSearch) {
    options.validateSearch = route.validateSearch
  }
  return createRoute(options as never)
}
